import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  useEffect(() => {
    API.get('/contact')
      .then((r) => setInquiries(r.data))
      .catch(() => toast.error('Failed to load inquiries'))
      .finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    try {
      await API.put(`/contact/${id}/read`, {});
      setInquiries((list) =>
        list.map((i) => (i._id === id ? { ...i, isRead: true } : i))
      );
    } catch {
      toast.error('Failed');
    }
  };

  const openInquiry = async (inquiry) => {
    setSelectedInquiry(inquiry);

    if (!inquiry.isRead) {
      await markRead(inquiry._id);
    }
  };

  return (
    <AdminLayout title="Customer Inquiries">
      {loading ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="inquiry-table-wrap">
            <table className="inquiry-table">
              <thead>
                <tr>
                  {['Name', 'Contact', 'Subject', 'Message', 'Date', 'Status', 'Action'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {inquiries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      No inquiries yet
                    </td>
                  </tr>
                ) : (
                  inquiries.map((i) => (
                    <tr
                      key={i._id}
                      className={i.isRead ? 'read-row' : 'unread-row'}
                    >
                      <td className="name-cell">{i.name}</td>
                      <td>{i.contact}</td>
                      <td>{i.subject || 'General Inquiry'}</td>

                      <td className="message-preview">
                        {i.message ? i.message.slice(0, 55) : '-'}
                        {i.message && i.message.length > 55 ? '...' : ''}
                      </td>

                      <td className="date-cell">
                        {new Date(i.createdAt).toLocaleDateString('en-IN')}
                      </td>

                      <td>
                        <span className={`tag ${i.isRead ? 'stock-in-stock' : 'stock-low'}`}>
                          {i.isRead ? 'Read' : 'Unread'}
                        </span>
                      </td>

                      <td>
                        <div className="action-buttons">
                          <button onClick={() => openInquiry(i)} className="view-btn">
                            View
                          </button>

                          {!i.isRead && (
                            <button onClick={() => markRead(i._id)} className="read-btn">
                              Mark Read
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {selectedInquiry && (
            <div className="modal-overlay" onClick={() => setSelectedInquiry(null)}>
              <div className="message-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Inquiry Details</h3>
                  <button onClick={() => setSelectedInquiry(null)}>×</button>
                </div>

                <div className="modal-body">
                  <div className="detail-row">
                    <span>Name</span>
                    <strong>{selectedInquiry.name}</strong>
                  </div>

                  <div className="detail-row">
                    <span>Contact</span>
                    <strong>{selectedInquiry.contact}</strong>
                  </div>

                  <div className="detail-row">
                    <span>Subject</span>
                    <strong>{selectedInquiry.subject || 'General Inquiry'}</strong>
                  </div>

                  <div className="detail-row">
                    <span>Date</span>
                    <strong>
                      {new Date(selectedInquiry.createdAt).toLocaleString('en-IN')}
                    </strong>
                  </div>

                  <div className="message-box">
                    <span>Message</span>
                    <p>{selectedInquiry.message || 'No message provided.'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <style>{`
            .inquiry-table-wrap {
              width: 100%;
              overflow-x: auto;
              background: white;
              border: 1px solid var(--border);
              border-radius: 6px;
            }

            .inquiry-table {
              width: 100%;
              min-width: 960px;
              border-collapse: collapse;
              background: white;
            }

            .inquiry-table th {
              padding: 13px 16px;
              font-size: 11px;
              letter-spacing: 2px;
              text-transform: uppercase;
              color: var(--text-light);
              text-align: left;
              border-bottom: 1px solid var(--border);
              background: var(--cream);
            }

            .inquiry-table td {
              padding: 13px 16px;
              font-size: 13px;
              border-bottom: 1px solid var(--border);
              vertical-align: top;
            }

            .unread-row {
              background: #FFFBF0;
            }

            .read-row {
              background: white;
            }

            .name-cell {
              font-weight: 600;
              color: var(--text-dark);
            }

            .message-preview {
              max-width: 260px;
              color: var(--text-mid);
              line-height: 1.5;
            }

            .date-cell {
              font-size: 12px;
              color: var(--text-light);
              white-space: nowrap;
            }

            .empty-cell {
              padding: 32px !important;
              text-align: center;
              color: var(--text-light);
            }

            .action-buttons {
              display: flex;
              gap: 8px;
              flex-wrap: wrap;
            }

            .view-btn,
            .read-btn {
              padding: 5px 11px;
              border: 1px solid var(--border);
              border-radius: 2px;
              background: white;
              font-size: 12px;
              cursor: pointer;
            }

            .view-btn {
              color: var(--maroon);
              border-color: var(--maroon);
            }

            .view-btn:hover {
              background: var(--maroon);
              color: white;
            }

            .read-btn:hover {
              background: var(--cream);
            }

            .modal-overlay {
              position: fixed;
              inset: 0;
              background: rgba(0,0,0,0.45);
              z-index: 9999;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
            }

            .message-modal {
              width: 100%;
              max-width: 560px;
              background: white;
              border-radius: 10px;
              box-shadow: 0 25px 80px rgba(0,0,0,0.25);
              overflow: hidden;
            }

            .modal-header {
              padding: 20px 24px;
              border-bottom: 1px solid var(--border);
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: var(--cream);
            }

            .modal-header h3 {
              font-family: 'Cormorant Garamond', serif;
              font-size: 28px;
              font-weight: 400;
              color: var(--maroon);
            }

            .modal-header button {
              border: none;
              background: transparent;
              font-size: 28px;
              cursor: pointer;
              color: var(--text-mid);
            }

            .modal-body {
              padding: 24px;
            }

            .detail-row {
              display: flex;
              justify-content: space-between;
              gap: 20px;
              padding: 10px 0;
              border-bottom: 1px solid var(--border);
            }

            .detail-row span,
            .message-box span {
              font-size: 11px;
              letter-spacing: 2px;
              text-transform: uppercase;
              color: var(--text-light);
            }

            .detail-row strong {
              font-size: 14px;
              color: var(--text-dark);
              text-align: right;
            }

            .message-box {
              margin-top: 18px;
            }

            .message-box p {
              margin-top: 10px;
              padding: 16px;
              background: var(--ivory);
              border: 1px solid var(--border);
              border-radius: 6px;
              line-height: 1.8;
              color: var(--text-mid);
              white-space: pre-wrap;
            }

            @media(max-width: 768px) {
              .message-modal {
                max-width: 100%;
              }

              .detail-row {
                flex-direction: column;
                gap: 4px;
              }

              .detail-row strong {
                text-align: left;
              }
            }
          `}</style>
        </>
      )}
    </AdminLayout>
  );
}